import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { PASSWORD_MIN, checkPassword } from "@brasilnovo/shared";
import { useAuth } from "../lib/auth.js";
import { Card, ErrorBox, PageHeader } from "../components/ui.js";

/**
 * Troca de senha. Uma tela só para dois caminhos:
 *
 *   voluntário  — o usuário escolhe trocar (link na barra lateral);
 *   obrigatório — a senha foi provisionada por um terceiro e o portal prende
 *                 o usuário aqui até a troca (ver `precisaTrocarSenha`).
 *
 * A diferença entre os dois é só o TOM: no obrigatório não há "cancelar" e o
 * texto explica por que ele está preso. Duplicar a tela para mudar um botão
 * criaria dois lugares para corrigir a mesma regra de senha.
 */
export function TrocarSenha() {
  const { user, changePassword, precisaTrocarSenha, logout } = useAuth();
  const navigate = useNavigate();
  const [atual, setAtual] = useState("");
  const [nova, setNova] = useState("");
  const [confirma, setConfirma] = useState("");
  const [erro, setErro] = useState<unknown>(null);
  const [salvando, setSalvando] = useState(false);
  const [pronto, setPronto] = useState(false);

  if (!user) return <Navigate to="/login" replace />;

  // Validação no cliente é ECO da regra do servidor (mesma função do shared),
  // para o usuário ver o problema enquanto digita — não é a checagem que vale.
  const problemas = nova ? checkPassword(nova).problemas : [];
  const confereConfirmacao = !confirma || nova === confirma;
  const podeSalvar =
    atual.length > 0 && nova.length > 0 && problemas.length === 0 &&
    nova === confirma && nova !== atual && !salvando;

  async function submeter(e: React.FormEvent) {
    e.preventDefault();
    setErro(null);
    setSalvando(true);
    try {
      await changePassword(atual, nova);
      setPronto(true);
      // Pequena pausa para a confirmação ser lida antes do redirecionamento.
      setTimeout(() => navigate("/", { replace: true }), 1200);
    } catch (e) {
      setErro(e);
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className="mx-auto max-w-xl">
      <PageHeader
        title={precisaTrocarSenha ? "Defina sua senha" : "Trocar senha"}
        subtitle={
          precisaTrocarSenha
            ? "Sua senha atual foi criada por outra pessoa. Escolha uma senha só sua para continuar."
            : "Escolha uma nova senha de acesso ao portal"
        }
      />

      {precisaTrocarSenha && (
        <div className="mb-5 rounded-xl bg-gold-50 px-4 py-3 text-sm text-gold-900 ring-1 ring-gold-200" data-testid="aviso-troca-obrigatoria">
          <b>Primeiro acesso.</b> Enquanto a senha provisória não for trocada, o restante do
          portal fica bloqueado — é a única forma de garantir que a sua senha seja conhecida
          só por você.
        </div>
      )}

      <Card>
        {pronto ? (
          <div className="py-6 text-center" data-testid="senha-trocada">
            <div className="mb-2 text-3xl">✓</div>
            <p className="font-semibold text-slate-800">Senha alterada.</p>
            <p className="mt-1 text-sm text-slate-500">
              As outras sessões foram encerradas. Levando você ao portal…
            </p>
          </div>
        ) : (
          <form onSubmit={submeter}>
            <label className="label">Senha atual</label>
            <input
              className="input mb-4" type="password" autoComplete="current-password"
              data-testid="senha-atual"
              value={atual} onChange={(e) => setAtual(e.target.value)} required
            />

            <label className="label">Nova senha</label>
            <input
              className="input" type="password" autoComplete="new-password"
              data-testid="senha-nova"
              value={nova} onChange={(e) => setNova(e.target.value)} required
            />
            <ul className="mb-4 mt-1 space-y-0.5 text-xs">
              {problemas.length > 0 ? (
                problemas.map((p) => <li key={p} className="text-amber-700">• {p}</li>)
              ) : (
                <li className="text-slate-400">
                  {nova ? "Senha aceita." : `Ao menos ${PASSWORD_MIN} caracteres, combinando 3 tipos (maiúscula, minúscula, número, símbolo).`}
                </li>
              )}
            </ul>

            <label className="label">Confirme a nova senha</label>
            <input
              className="input" type="password" autoComplete="new-password"
              data-testid="senha-confirma"
              value={confirma} onChange={(e) => setConfirma(e.target.value)} required
            />
            {!confereConfirmacao && (
              <p className="mb-4 mt-1 text-xs text-red-600">As duas senhas não conferem.</p>
            )}
            {confereConfirmacao && <div className="mb-4" />}

            {erro != null && <div className="mb-4"><ErrorBox error={erro} /></div>}

            <div className="flex items-center justify-end gap-2">
              {precisaTrocarSenha ? (
                // Sem "cancelar": não há para onde voltar. Sair é a única
                // alternativa legítima a definir a senha.
                <button
                  type="button" className="btn-ghost"
                  onClick={async () => { await logout(); navigate("/login", { replace: true }); }}
                >
                  Sair
                </button>
              ) : (
                <button type="button" className="btn-ghost" onClick={() => navigate(-1)}>
                  Cancelar
                </button>
              )}
              <button className="btn-primary" disabled={!podeSalvar} data-testid="salvar-senha">
                {salvando ? "Salvando…" : "Salvar nova senha"}
              </button>
            </div>
          </form>
        )}
      </Card>
    </div>
  );
}
